import { useState, useEffect, useCallback, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";

import { FONTS, THEMES, makeBaseStyles } from "./theme";
import { TOOL_GROUPS, ALL_TOOLS } from "./routes";
import RelatedTools from "./RelatedTools";
import FaqPage     from "./FaqPage";
import PrivacyPage from "./PrivacyPage";
import FreeDeveloperTools       from "./pages/FreeDeveloperTools";
import JsonFormatterAlternative from "./pages/alternatives/JsonFormatterAlternative";
import CyberChefAlternative     from "./pages/alternatives/CyberChefAlternative";
import DcodeAlternative         from "./pages/alternatives/DcodeAlternative";
import BestFreeDevTools2026     from "./pages/blog/BestFreeDevTools2026";

import JsonToolkit        from "./JsonToolkit";
import FakeDataGenerator  from "./FakeDataGenerator";
import FileConverter      from "./FileConverter";
import EncodingToolkit    from "./EncodingToolkit";
import GeneratorsToolkit  from "./GeneratorsToolkit";
import HashToolkit        from "./HashToolkit";
import TimeToolkit        from "./TimeToolkit";
import TextToolkit        from "./TextToolkit";
import CompressionToolkit from "./CompressionToolkit";
import {
  ToolIcon,
  HomeNavIcon, ExportsNavIcon, PrivacyNavIcon, FaqNavIcon, PortfolioNavIcon,
  FeaturePrivacyIcon, FeatureInstantIcon, FeatureNoSignupIcon, FeatureOfflineIcon,
} from "./ToolIcons";

// ─── LOCALSTORAGE HELPERS ────────────────────────────────────────────────────
function lsGet(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ─── TOOL COMPONENTS MAP ─────────────────────────────────────────────────────
const TOOL_COMPONENTS = {
  "json-toolkit":   (t, h) => <JsonToolkit        t={t} onExport={h} />,
  "fake-data":      (t, h) => <FakeDataGenerator  t={t} onExport={h} />,
  "file-converter": (t, h) => <FileConverter      t={t} onExport={h} />,
  "encoding":       (t, h) => <EncodingToolkit    t={t} onExport={h} />,
  "generators":     (t, h) => <GeneratorsToolkit  t={t} onExport={h} />,
  "hash-tools":     (t, h) => <HashToolkit        t={t} onExport={h} />,
  "time-tools":     (t, h) => <TimeToolkit        t={t} onExport={h} />,
  "text-tools":     (t, h) => <TextToolkit        t={t} onExport={h} />,
  "compression":    (t, h) => <CompressionToolkit t={t} onExport={h} />,
};

function formatTime(ts) {
  const diff = Date.now() - ts;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

// ─── EXPORT TOAST ────────────────────────────────────────────────────────────
function ExportToast({ toast, onDone }) {
  const [leaving, setLeaving] = useState(false);
  const tool = ALL_TOOLS.find(x =>
    toast.tool && (toast.tool === x.name || toast.tool.startsWith(x.name.split(" ")[0]))
  );

  useEffect(() => {
    const tid = setTimeout(() => {
      setLeaving(true);
      setTimeout(onDone, 350);
    }, 3000);
    return () => clearTimeout(tid);
  }, [onDone]);

  const rowLabel = toast.rows > 0
    ? `${toast.rows.toLocaleString()} row${toast.rows !== 1 ? "s" : ""}`
    : "1 file";

  return (
    <div className={`export-toast ${leaving ? "toast-leave" : "toast-enter"}`}>
      <div className="toast-icon-wrap">{tool?.icon ?? "📦"}</div>
      <div className="toast-body">
        <div className="toast-title">Export complete!</div>
        <div className="toast-sub">{toast.tool} · {rowLabel}</div>
      </div>
      <span className="toast-check">✓</span>
      <div className="toast-progress-bar" />
    </div>
  );
}

function ExportToastContainer({ toasts, setToasts }) {
  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, [setToasts]);

  if (!toasts.length) return null;
  return (
    <div className="toast-container" aria-live="polite" aria-label="Export notifications">
      {toasts.map(toast => (
        <ExportToast key={toast.id} toast={toast} onDone={() => remove(toast.id)} />
      ))}
    </div>
  );
}

// ─── EXPORT HISTORY PAGE ──────────────────────────────────────────────────────
function ExportHistoryPage({ t, exports, onClear }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  // Unique tool names for filter chips
  const toolNames = [...new Set(exports.map(e => e.tool).filter(Boolean))];

  const filtered = filter === "all"
    ? exports
    : exports.filter(e => e.tool === filter);

  // Stats
  const totalRows = exports.reduce((s, e) => s + (e.rows || 0), 0);
  const topTool   = toolNames.length
    ? toolNames.reduce((a, b) =>
        exports.filter(e => e.tool === b).length > exports.filter(e => e.tool === a).length ? b : a
      )
    : null;

  // Group by date label
  function dateLabel(ts) {
    const d    = new Date(ts);
    const now  = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate()) return "Today";
    if (diff < 172800000) return "Yesterday";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  // Build grouped list
  const groups = [];
  let lastLabel = null;
  filtered.forEach((ex, i) => {
    const label = dateLabel(ex.timestamp);
    if (label !== lastLabel) {
      groups.push({ type: "divider", label, key: `d-${i}` });
      lastLabel = label;
    }
    groups.push({ type: "item", ex, key: `i-${i}`, idx: i });
  });

  const tool4ex = (ex) => ALL_TOOLS.find(x =>
    ex.tool && (ex.tool === x.name || ex.tool.startsWith(x.name.split(" ")[0]))
  );

  return (
    <>
      <Helmet>
        <title>Export History — miniDevToolkit</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="export-history-page">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: t.textMuted, fontFamily: "JetBrains Mono", marginBottom: 6 }}>
              miniDevToolkit
            </div>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: t.textPrimary, letterSpacing: "-0.03em", margin: 0 }}>
              Export History
            </h2>
          </div>
          {exports.length > 0 && (
            <button
              onClick={() => { if (window.confirm("Clear all export history?")) onClear(); }}
              style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 10, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
              onMouseEnter={e => { e.target.style.background = "rgba(239,68,68,0.18)"; }}
              onMouseLeave={e => { e.target.style.background = "rgba(239,68,68,0.10)"; }}
            >
              Clear All
            </button>
          )}
        </div>

        {/* Stats */}
        {exports.length > 0 && (
          <div className="export-history-stats">
            <div className="export-stat-card">
              <div className="export-stat-value">{exports.length}</div>
              <div className="export-stat-label">Total Exports</div>
            </div>
            <div className="export-stat-card">
              <div className="export-stat-value">{totalRows.toLocaleString()}</div>
              <div className="export-stat-label">Total Rows</div>
            </div>
            <div className="export-stat-card">
              <div className="export-stat-value" style={{ fontSize: 18, paddingTop: 7 }}>
                {topTool
                  ? (ALL_TOOLS.find(x => x.name === topTool || topTool.startsWith(x.name.split(" ")[0]))?.icon ?? "📦") + " " + topTool.split(" ")[0]
                  : "—"}
              </div>
              <div className="export-stat-label">Most Used Tool</div>
            </div>
          </div>
        )}

        {/* Filter chips */}
        {exports.length > 0 && (
          <div className="export-history-filters">
            <button
              className={`export-filter-chip ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({exports.length})
            </button>
            {toolNames.map(name => (
              <button
                key={name}
                className={`export-filter-chip ${filter === name ? "active" : ""}`}
                onClick={() => setFilter(name)}
              >
                {ALL_TOOLS.find(x => x.name === name || name.startsWith(x.name.split(" ")[0]))?.icon ?? "📦"}{" "}
                {name.split("→")[0].trim()} ({exports.filter(e => e.tool === name).length})
              </button>
            ))}
          </div>
        )}

        {/* List */}
        {filtered.length === 0 ? (
          <div className="export-empty-state">
            <div className="export-empty-icon">📦</div>
            <div className="export-empty-title">No exports yet</div>
            <div className="export-empty-sub" style={{ marginBottom: 20 }}>
              Use any tool to generate or export data — it will appear here.
            </div>
            <button className="btn-primary" onClick={() => navigate("/")} style={{ padding: "10px 24px", fontSize: 13 }}>
              Browse Tools →
            </button>
          </div>
        ) : (
          <div className="export-history-list">
            {groups.map((g, gi) =>
              g.type === "divider" ? (
                <div key={g.key} className="export-history-date-divider" style={{ animationDelay: `${gi * 0.03}s` }}>
                  <div className="export-history-date-line" />
                  <span className="export-history-date-label">{g.label}</span>
                  <div className="export-history-date-line" />
                </div>
              ) : (
                <div
                  key={g.key}
                  className="export-history-item"
                  style={{ animationDelay: `${g.idx * 0.04}s`, cursor: "default" }}
                  onClick={() => {
                    const tl = tool4ex(g.ex);
                    if (tl) navigate(tl.path);
                  }}
                  role={tool4ex(g.ex) ? "button" : undefined}
                  tabIndex={tool4ex(g.ex) ? 0 : undefined}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      const tl = tool4ex(g.ex);
                      if (tl) navigate(tl.path);
                    }
                  }}
                >
                  <div className="export-history-tool-icon">
                    {tool4ex(g.ex)?.icon ?? "📦"}
                  </div>
                  <div className="export-history-meta">
                    <div className="export-history-tool-name">{g.ex.tool}</div>
                    <div className="export-history-detail">
                      {new Date(g.ex.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      {" · "}
                      {formatTime(g.ex.timestamp)}
                    </div>
                  </div>
                  {g.ex.rows > 0 && (
                    <div className="export-history-rows">
                      {g.ex.rows.toLocaleString()} rows
                    </div>
                  )}
                  {tool4ex(g.ex) && <span className="export-history-go">›</span>}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── LOGO MARK (SVG) ─────────────────────────────────────────────────────────
export function LogoMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="lm-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#F05A41"/>
          <stop offset="100%" stopColor="#D8A056"/>
        </linearGradient>
      </defs>
      <rect width="36" height="36" rx="10" fill="url(#lm-grad)"/>
      <path d="M12 10 Q9 10 9 13 L9 15.5 Q9 18 7 18 Q9 18 9 20.5 L9 23 Q9 26 12 26" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M24 10 Q27 10 27 13 L27 15.5 Q27 18 29 18 Q27 18 27 20.5 L27 23 Q27 26 24 26" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M20 12 L16.5 18.5 H19.5 L16 24" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

// ─── SIDEBAR STYLES ───────────────────────────────────────────────────────────
function makeSidebarStyles(t, sidebarOpen) {
  const SW  = 260;
  const SWC = 64;
  const isDark      = t.bg.startsWith("#0");
  const iconIdle    = isDark ? "#64748b" : "rgba(60,70,82,0.55)";
  const iconHover   = isDark ? "#d9e3f2" : "rgba(60,70,82,0.90)";
  const iconActiveBg= isDark ? "rgba(255,255,255,0.05)" : "rgba(240,90,65,0.06)";
  const sbBg        = isDark ? "#080d14" : t.bgPanel;

  return `
.app-shell { display:flex; min-height:100vh; background:${t.bg}; }

/* ── SIDEBAR ── */
.sidebar {
  position:fixed; top:0; left:0; bottom:0;
  width:${sidebarOpen ? SW : SWC}px;
  background:${sbBg};
  display:flex; flex-direction:column;
  z-index:300;
  transition:width 0.22s cubic-bezier(0.4,0,0.2,1);
  overflow:hidden;
}
.sb-header {
  display:flex; align-items:center; height:64px; flex-shrink:0;
  padding:0 ${sidebarOpen ? 16 : 0}px;
  justify-content:${sidebarOpen ? "flex-start" : "center"};
  gap:10px; overflow:hidden;
  transition:padding 0.22s, justify-content 0.22s;
}
.sb-logo-icon {
  width:36px; height:36px; flex-shrink:0; cursor:pointer;
  text-decoration:none; transition:transform 0.15s; display:block;
}
.sb-logo-icon:hover { transform:scale(1.05); }
.sb-brand {
  flex:1; min-width:0; overflow:hidden;
  opacity:${sidebarOpen ? 1 : 0};
  max-width:${sidebarOpen ? 175 : 0}px;
  transition:opacity 0.18s, max-width 0.22s;
  white-space:nowrap;
}
.sb-brand-name {
  font-size:14px; font-weight:800; line-height:1.2; letter-spacing:-0.03em;
  background:linear-gradient(135deg, #F05A41 0%, #D8A056 100%);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
}
.sb-brand-sub  { font-size:9px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; margin-top:1px; }
.sb-toggle {
  flex-shrink:0; width:26px; height:26px;
  display:flex; align-items:center; justify-content:center;
  border-radius:6px; border:1px solid ${t.border};
  background:none; cursor:pointer;
  color:${iconIdle}; font-size:14px; font-weight:700;
  transition:all 0.15s; margin-left:auto;
  opacity:${sidebarOpen ? 1 : 0};
  pointer-events:${sidebarOpen ? "all" : "none"};
}
.sb-toggle:hover { background:${iconActiveBg}; color:${t.textAccent}; border-color:${t.borderActive}; }

/* ── NEW TOOL BUTTON ── */
.sb-new-tool-btn {
  margin:0 8px 8px;
  padding:10px 14px;
  background:linear-gradient(135deg, #F05A41 0%, #D8A056 100%);
  border:none; border-radius:10px;
  font-family:'Plus Jakarta Sans',sans-serif;
  font-size:12px; font-weight:700; color:#fff; cursor:pointer;
  display:flex; align-items:center;
  justify-content:${sidebarOpen ? "flex-start" : "center"};
  gap:8px; transition:all 0.15s;
  box-shadow:0 4px 14px rgba(240,90,65,0.20);
  overflow:hidden; white-space:nowrap;
  width:calc(100% - 16px);
  flex-shrink:0; text-decoration:none;
}
.sb-new-tool-btn:hover { box-shadow:0 8px 24px rgba(240,90,65,0.35); transform:translateY(-1px); }
.sb-ntb-icon { font-size:16px; font-weight:400; flex-shrink:0; line-height:1; }
.sb-ntb-label {
  overflow:hidden; max-width:${sidebarOpen ? 110 : 0}px;
  transition:max-width 0.22s; white-space:nowrap;
}

.sb-body {
  flex:1; overflow-y:auto; overflow-x:hidden;
  padding:4px 8px 4px;
  scrollbar-width:thin; scrollbar-color:${t.scrollThumb} transparent;
}
.sb-body::-webkit-scrollbar { width:3px; }
.sb-body::-webkit-scrollbar-thumb { background:${t.scrollThumb}; border-radius:3px; }

/* ── NAV ITEMS ── */
.sb-nav-item {
  display:flex; align-items:center;
  width:100%; height:42px; padding:0;
  border-radius:${sidebarOpen ? "8px 0 0 8px" : "8px"}; border:none; background:none;
  cursor:pointer; margin-bottom:2px;
  transition:background 0.15s, color 0.15s;
  font-family:'Plus Jakarta Sans',sans-serif;
  overflow:hidden; white-space:nowrap; position:relative;
  text-decoration:none;
  justify-content:${sidebarOpen ? "flex-start" : "center"};
}
.sb-nav-item:hover { background:rgba(255,255,255,0.07); }
.sb-nav-item.active { background:rgba(255,255,255,0.05); }
.sb-nav-item.active::after {
  content:""; position:absolute; right:0; top:6px; bottom:6px;
  width:4px; border-radius:4px 0 0 4px;
  background:linear-gradient(180deg, #F05A41 0%, #D8A056 100%);
}
.sb-home-item { margin-bottom:2px; }

.sb-icon-cell {
  width:${SWC}px; height:42px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
}
.sb-icon-box {
  width:34px; height:34px; border-radius:9px;
  display:flex; align-items:center; justify-content:center;
  font-size:15px; font-family:'JetBrains Mono',monospace; font-weight:700;
  color:${iconIdle}; transition:all 0.15s;
}
.sb-nav-item:hover  .sb-icon-box { color:${iconHover}; }
.sb-nav-item.active .sb-icon-box { color:#ffb4a6; }

.sb-label-cell {
  flex:1; min-width:0; padding-right:12px; overflow:hidden;
  opacity:${sidebarOpen ? 1 : 0}; transition:opacity 0.15s;
}
.sb-tool-name {
  font-size:13px; font-weight:500; color:${iconIdle};
  text-align:left; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.sb-nav-item:hover  .sb-tool-name { color:${iconHover}; }
.sb-nav-item.active .sb-tool-name { color:#ffb4a6; font-weight:700; }

.sb-divider { height:1px; background:rgba(255,255,255,0.05); margin:6px 8px 8px; }
.sb-group-label {
  font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.12em;
  color:${t.textMuted};
  padding:8px 0 4px ${sidebarOpen ? 16 : 0}px;
  text-align:${sidebarOpen ? "left" : "center"};
  white-space:nowrap; overflow:hidden;
  opacity:${sidebarOpen ? 0.7 : 0};
  height:${sidebarOpen ? "auto" : 0};
  transition:opacity 0.18s, height 0.22s, padding 0.22s;
  line-height:1;
}

/* ── SIDEBAR BOTTOM ── */
.sb-bottom {
  flex-shrink:0; padding:0 8px 12px;
  border-top:1px solid rgba(255,255,255,0.05);
  margin-top:4px; padding-top:8px;
}

/* Tooltip (collapsed only) */
.sb-tooltip {
  position:absolute; left:calc(100% + 10px);
  top:50%; transform:translateY(-50%);
  background:${t.bgPanel}; border:1px solid ${t.border};
  border-radius:7px; padding:5px 11px;
  font-size:12px; font-family:'Plus Jakarta Sans',sans-serif;
  color:${t.textPrimary}; white-space:nowrap;
  pointer-events:none; opacity:0;
  transition:opacity 0.15s; z-index:400;
  box-shadow:0 4px 18px rgba(0,0,0,0.25);
}
.sb-nav-item:hover .sb-tooltip { opacity:${sidebarOpen ? 0 : 1}; }

/* ── MAIN CONTENT ── */
.main-content {
  flex:1; margin-left:${sidebarOpen ? SW : SWC}px;
  transition:margin-left 0.22s cubic-bezier(0.4,0,0.2,1);
  min-width:0; display:flex; flex-direction:column;
}

/* ── TOP BAR ── */
.top-bar {
  height:64px; display:flex; align-items:center;
  justify-content:space-between; padding:0 1.5rem; gap:16px;
  background:${isDark ? "rgba(8,13,20,0.82)" : "rgba(251,251,251,0.88)"};
  backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
  box-shadow:0 12px 32px rgba(240,90,65,0.06);
  flex-shrink:0; position:sticky; top:0; z-index:100;
}
.top-bar-left { display:flex; align-items:center; gap:16px; flex:1; min-width:0; }
.top-bar-right { display:flex; align-items:center; gap:8px; flex-shrink:0; }

/* Search */
.top-bar-search-wrap { position:relative; flex-shrink:0; }
.top-bar-search-icon {
  position:absolute; left:10px; top:50%; transform:translateY(-50%);
  font-size:13px; color:${t.textMuted}; pointer-events:none; line-height:1;
}
.top-bar-search {
  background:${t.bgInput}; border:1px solid ${t.border};
  border-radius:8px; padding:7px 14px 7px 32px;
  font-family:'JetBrains Mono',monospace; font-size:12px;
  color:${t.textPrimary}; width:220px; outline:none;
  transition:border-color 0.15s, box-shadow 0.15s;
}
.top-bar-search::placeholder { color:${t.textMuted}; }
.top-bar-search:focus { border-color:${t.borderActive}; box-shadow:0 0 0 3px rgba(240,90,65,0.10); width:260px; }

/* Quick nav links */
.top-bar-quicknav { display:flex; gap:20px; align-items:center; }
.top-bar-qlink {
  font-size:13px; font-weight:500; color:${t.textMuted};
  text-decoration:none; transition:color 0.15s;
  padding-bottom:3px; border-bottom:2px solid transparent;
  white-space:nowrap;
}
.top-bar-qlink:hover { color:#f7bc6f; }
.top-bar-qlink.qlink-active { color:#ffb4a6; font-weight:700; border-bottom-color:#F05A41; }

/* Icon buttons */
.top-bar-icon-btn {
  width:34px; height:34px; display:flex; align-items:center; justify-content:center;
  background:none; border:none; cursor:pointer; border-radius:8px;
  color:${t.textMuted}; font-size:16px;
  transition:color 0.15s, background 0.15s;
}
.top-bar-icon-btn:hover { color:${t.textPrimary}; background:rgba(255,255,255,0.06); }

/* Top bar separator */
.top-bar-sep { width:1px; height:22px; background:${t.border}; flex-shrink:0; }

/* Export button */
.top-bar-export-btn {
  background:${isDark ? t.bgPanelHead : t.bgPanel}; border:1px solid ${t.border};
  border-radius:8px; padding:7px 14px;
  font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700;
  color:${isDark ? "#ffb4a6" : t.textAccent}; cursor:pointer; transition:all 0.15s;
  white-space:nowrap;
}
.top-bar-export-btn:hover { background:${t.bgCardHover}; border-color:${t.borderActive}; }

/* Avatar */
.top-bar-avatar {
  width:32px; height:32px; border-radius: 50%;
  background:linear-gradient(135deg, #F05A41 0%, #D8A056 100%);
  border:2px solid rgba(90,65,60,0.25);
  display:flex; align-items:center; justify-content:center;
  font-size:10px; font-weight:700; color:#fff; flex-shrink:0;
  font-family:'JetBrains Mono',monospace; cursor:pointer;
  letter-spacing:0.05em;
}

/* Search dropdown */
.search-dropdown {
  position:absolute; top:calc(100% + 6px); left:0; right:0;
  background:${t.bgPanel}; border:1px solid ${t.border};
  border-radius:10px; overflow:hidden; z-index:600;
  box-shadow:0 16px 40px rgba(0,0,0,0.30);
  animation:slide-down 0.15s ease;
}
.search-dropdown-item {
  display:flex; align-items:center; gap:10px;
  padding:10px 14px; cursor:pointer; text-decoration:none;
  transition:background 0.1s;
}
.search-dropdown-item:hover { background:${t.bgChipActive}; }
.search-dropdown-item-icon { font-size:16px; flex-shrink:0; }
.search-dropdown-item-name { font-size:13px; font-weight:600; color:${t.textPrimary}; }
.search-dropdown-item-desc { font-size:11px; color:${t.textMuted}; }

/* Page badge */
.page-badge {
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:20px;
  background:${t.bgChipActive}; border:1px solid rgba(240,90,65,0.20);
  font-size:10px; font-weight:700; color:${t.textAccent};
  text-transform:uppercase; letter-spacing:0.1em;
  font-family:'JetBrains Mono',monospace;
}

/* ── MOBILE OVERLAY ── */
.sidebar-overlay {
  display:none; position:fixed; inset:0;
  background:rgba(0,0,0,0.55); z-index:299;
  backdrop-filter:blur(4px);
  animation:ov-in 0.2s ease;
}
@keyframes ov-in { from{opacity:0} to{opacity:1} }

/* ── RESPONSIVE ── */
@media (max-width:768px) {
  .sidebar {
    width:${SW}px !important;
    transform:translateX(-100%);
    transition:transform 0.25s cubic-bezier(0.4,0,0.2,1) !important;
  }
  .sidebar.mobile-open { transform:translateX(0); }
  .sidebar-overlay     { display:block; }
  .main-content        { margin-left:0 !important; }
  .sb-brand            { opacity:1 !important; max-width:175px !important; }
  .sb-toggle           { display:none; }
  .sb-group-label      { opacity:0.7 !important; height:auto !important; padding-left:16px !important; }
  .sb-label-cell       { opacity:1 !important; }
  .sb-nav-item         { justify-content:flex-start !important; border-radius:8px 0 0 8px !important; }
  .sb-header           { padding:0 16px !important; justify-content:flex-start !important; }
  .top-bar-quicknav    { display:none; }
  .top-bar-search-wrap { display:none; }
  .sb-ntb-label        { max-width:110px !important; }
  .sb-new-tool-btn     { justify-content:center !important; }
}
@media (max-width:600px) {
  .top-bar { padding:0 1rem; gap:8px; }
  .top-bar-icon-btn { display:none; }
  .top-bar-sep { display:none; }
}

/* ── HAMBURGER ── */
.hamburger-btn {
  width:44px; height:44px; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:5px;
  border-radius:8px; border:1px solid ${t.border};
  background:none; cursor:pointer; transition:all 0.15s; flex-shrink:0;
}
.hamburger-btn:hover { background:${iconActiveBg}; border-color:${t.borderActive}; }
.hamburger-btn span {
  display:block; width:16px; height:1.5px;
  background:${iconIdle}; border-radius:2px; transition:all 0.25s;
}
.hamburger-btn.open span:nth-child(1) { transform:translateY(6.5px) rotate(45deg); }
.hamburger-btn.open span:nth-child(2) { opacity:0; transform:scaleX(0); }
.hamburger-btn.open span:nth-child(3) { transform:translateY(-6.5px) rotate(-45deg); }

/* ── FLOATING THEME TOGGLE ── */
.float-theme-btn {
  position:fixed; top:55px; right:18px; z-index:500;
  display:flex; align-items:center; gap:7px;
  height:34px; padding:0 12px 0 10px;
  border-radius:20px; border:1px solid ${t.border};
  background:${isDark ? "rgba(8,13,20,0.92)" : "rgba(255,255,255,0.92)"};
  cursor:pointer;
  font-family:'JetBrains Mono',monospace; font-size:11px; color:${t.textSecondary};
  box-shadow:0 2px 12px rgba(0,0,0,0.20);
  transition:all 0.2s; white-space:nowrap;
  backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);
}
.float-theme-btn:hover {
  border-color:${t.borderActive}; color:${t.textAccent};
  box-shadow:0 4px 20px rgba(240,90,65,0.22);
  transform:translateY(-1px);
}
.float-theme-btn:active { transform:scale(0.97); }
.float-theme-icon { font-size:14px; display:inline-block; transition:transform 0.45s; line-height:1; }
.float-theme-btn:hover .float-theme-icon { transform:rotate(22deg); }
@media (max-width:480px) {
  .float-theme-btn .float-theme-label { display:none; }
  .float-theme-btn { padding:0 10px; }
}

/* ── RECENT EXPORTS TABLE ── */
.exports-table-wrap { overflow-x:auto; }
.exports-table {
  width:100%; border-collapse:collapse;
  font-family:'JetBrains Mono',monospace; font-size:12px;
}
.exports-table th {
  padding:10px 16px; font-weight:600; font-size:10px;
  text-transform:uppercase; letter-spacing:0.1em;
  color:${t.textMuted}; text-align:left;
  border-bottom:1px solid rgba(255,255,255,0.05);
}
.exports-table td {
  padding:14px 16px; border-bottom:1px solid rgba(255,255,255,0.04);
  color:${t.textSecondary}; transition:background 0.1s;
}
.exports-table tr:hover td { background:rgba(255,255,255,0.03); }
.exports-table .col-tool { color:${t.textPrimary}; font-weight:700; }

/* ── FAQ TEASER ── */
.faq-teaser { padding:2rem 1.5rem 3rem; }
.faq-teaser-inner { max-width:720px; margin:0 auto; }
.faq-teaser-item {
  background:${t.bgPanel}; border:1px solid ${t.border}; border-radius:12px;
  padding:14px 18px; margin-bottom:8px; cursor:pointer;
  transition:border-color 0.15s, background 0.15s; display:block; text-decoration:none;
}
.faq-teaser-item:hover { border-color:${t.borderActive}; background:${t.bgChipActive}; }
.faq-teaser-q { font-size:13px; font-weight:600; color:${t.textPrimary}; margin-bottom:5px; }
.faq-teaser-a { font-size:12px; color:${t.textSecondary}; line-height:1.6; }

/* ── PRO TIP CARD ── */
.pro-tip-card {
  background:${t.bgPanelHead}; border:1px solid ${t.border}; border-radius:12px;
  padding:20px; box-shadow:0 12px 32px rgba(240,90,65,0.06);
}
.pro-tip-title { font-size:14px; font-weight:700; color:${t.textPrimary}; margin-bottom:10px; display:flex; align-items:center; gap:6px; }
.pro-tip-card p { font-size:13px; color:${t.textSecondary}; line-height:1.6; }
kbd {
  display:inline-block; padding:2px 7px;
  background:${t.bgPanel}; border:1px solid ${t.border};
  border-radius:5px; font-size:11px; font-family:'JetBrains Mono',monospace;
  color:${t.textSecondary};
}

/* ── STITCH HERO ── */
.stitch-hero {
  position:relative; padding:5rem 3rem 4rem;
  overflow:hidden;
}
.stitch-hero-blob1 {
  position:absolute; top:0; right:0;
  width:500px; height:500px;
  background:rgba(240,90,65,0.05);
  border-radius:50%; filter:blur(120px);
  pointer-events:none; z-index:0;
  animation:blob-drift 18s ease-in-out infinite;
}
.stitch-hero-blob2 {
  position:absolute; bottom:0; left:0;
  width:400px; height:400px;
  background:rgba(216,160,86,0.05);
  border-radius:50%; filter:blur(100px);
  pointer-events:none; z-index:0;
  animation:blob-drift 22s ease-in-out infinite reverse;
}
.stitch-hero-inner { position:relative; z-index:1; max-width:900px; }
.stitch-hero h2 {
  font-size:clamp(36px,5.5vw,64px);
  font-weight:800; letter-spacing:-0.04em;
  line-height:1.05; margin-bottom:1.25rem;
  color:${t.textPrimary};
}
.stitch-hero h2 em {
  font-style:normal;
  background:linear-gradient(135deg, #F05A41 0%, #D8A056 100%);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
}
.stitch-hero-sub {
  font-size:clamp(14px,2vw,18px); color:${t.textSecondary};
  max-width:560px; line-height:1.7; margin-bottom:2.5rem; font-weight:500;
}
.stitch-hero-ctas { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:2rem; }
.stitch-trust {
  display:flex; align-items:center; gap:24px; flex-wrap:wrap; margin-top:1.5rem;
}
.stitch-trust-label {
  font-size:10px; font-family:'JetBrains Mono',monospace; color:${t.textMuted};
  text-transform:uppercase; letter-spacing:0.14em; white-space:nowrap;
}
.stitch-trust-badges { display:flex; gap:20px; align-items:center; opacity:0.45; }
.stitch-trust-badge { font-size:14px; font-weight:800; letter-spacing:-0.03em; color:${t.textPrimary}; }

/* ── STITCH FEATURE STRIP ── */
.stitch-features { padding:0 3rem 3rem; }
.stitch-features-grid {
  display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:16px;
}
.stitch-feature-card {
  background:${t.bgPanel}; border:1px solid ${t.border};
  border-radius:12px; padding:24px 20px;
  transition:border-color 0.2s, transform 0.2s;
}
.stitch-feature-card:hover { border-color:rgba(240,90,65,0.25); transform:translateY(-2px); }
.stitch-feature-icon {
  width:44px; height:44px; border-radius:10px;
  background:linear-gradient(135deg, #F05A41 0%, #D8A056 100%);
  display:flex; align-items:center; justify-content:center;
  font-size:20px; color:#fff; margin-bottom:14px;
  box-shadow:0 4px 14px rgba(240,90,65,0.20);
}
.stitch-feature-title { font-size:15px; font-weight:700; color:${t.textPrimary}; margin-bottom:6px; }
.stitch-feature-desc { font-size:13px; color:${t.textSecondary}; line-height:1.5; }

/* ── STITCH TOOLS SECTION ── */
.stitch-tools-section { padding:2rem 3rem 2rem; }
.stitch-tools-header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:20px; }
.stitch-tools-category-label {
  font-size:11px; font-weight:700; font-family:'JetBrains Mono',monospace;
  color:#f7bc6f; letter-spacing:0.18em; text-transform:uppercase; margin-bottom:4px;
}
.stitch-tools-category-title { font-size:28px; font-weight:800; letter-spacing:-0.03em; color:${t.textPrimary}; }
.stitch-tools-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:10px; margin-bottom:32px; }
.stitch-tool-card {
  background:${t.bgPanel}; border:1px solid ${t.border}; border-radius:12px;
  padding:16px 18px; cursor:pointer; display:flex; align-items:center;
  justify-content:space-between; gap:12px;
  transition:background 0.15s, border-color 0.15s;
  text-decoration:none;
}
.stitch-tool-card:hover { background:${t.bgCardHover}; border-color:rgba(240,90,65,0.20); }
.stitch-tool-card-left { display:flex; align-items:center; gap:14px; min-width:0; }
.stitch-tool-icon-box {
  width:40px; height:40px; border-radius:8px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:18px;
}
.stitch-tool-name { font-size:15px; font-weight:700; color:${t.textPrimary}; margin-bottom:2px; }
.stitch-tool-desc { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; }
.stitch-tool-chevron {
  font-size:18px; color:${t.textMuted}; flex-shrink:0;
  transition:color 0.15s, transform 0.15s;
}
.stitch-tool-card:hover .stitch-tool-chevron { color:#ffb4a6; transform:translateX(3px); }

/* ── EXPORTS & BOTTOM ── */
.stitch-bottom-grid { display:grid; grid-template-columns:2fr 1fr; gap:24px; padding:0 3rem 3rem; }
.stitch-exports-card { background:${t.bgPanel}; border:1px solid ${t.border}; border-radius:12px; padding:24px; }
.stitch-exports-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
.stitch-exports-title { font-size:16px; font-weight:700; color:${t.textPrimary}; }
.stitch-exports-time { font-size:10px; font-family:'JetBrains Mono',monospace; color:${t.textMuted}; text-transform:uppercase; letter-spacing:0.12em; }
.stitch-export-empty { text-align:center; padding:32px 0; color:${t.textMuted}; font-size:13px; font-family:'JetBrains Mono',monospace; }
.stitch-export-table { width:100%; border-collapse:collapse; font-family:'JetBrains Mono',monospace; font-size:12px; }
.stitch-export-table th { padding:8px 12px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.10em; color:${t.textMuted}; text-align:left; border-bottom:1px solid rgba(255,255,255,0.05); padding-bottom:12px; }
.stitch-export-table td { padding:14px 12px; border-bottom:1px solid rgba(255,255,255,0.04); color:${t.textSecondary}; transition:background 0.1s; }
.stitch-export-table tr:hover td { background:rgba(255,255,255,0.03); }
.stitch-export-table .col-tool { color:${t.textPrimary}; font-weight:700; }
.stitch-side-cards { display:flex; flex-direction:column; gap:16px; }

@media(max-width:900px) {
  .stitch-hero { padding:3rem 1.5rem 2.5rem; }
  .stitch-features { padding:0 1.5rem 2rem; }
  .stitch-tools-section { padding:1.5rem 1.5rem 1.5rem; }
  .stitch-bottom-grid { grid-template-columns:1fr; padding:0 1.5rem 3rem; }
}
@media(max-width:600px) {
  .stitch-tools-grid { grid-template-columns:1fr; }
  .stitch-features-grid { grid-template-columns:1fr 1fr; }
  .stitch-trust { flex-direction:column; align-items:flex-start; gap:8px; }
}
@media(max-width:400px) {
  .stitch-features-grid { grid-template-columns:1fr; }
}

/* ── FOOTER GRID ── */
.footer-grid { display:grid; grid-template-columns:1.5fr 1fr 1fr 1fr; gap:2rem; margin-bottom:2rem; align-items:start; }
@media(max-width:900px) { .footer-grid { grid-template-columns:1fr 1fr; } }
@media(max-width:500px) { .footer-grid { grid-template-columns:1fr; } }

/* ── EXPORT TOAST ── */
.toast-container {
  position:fixed; bottom:28px; right:28px;
  z-index:9999; display:flex; flex-direction:column-reverse; gap:10px;
  align-items:flex-end; pointer-events:none;
}
.export-toast {
  display:flex; align-items:center; gap:12px;
  background:${isDark ? "#141a24" : "#fff"};
  border:1px solid ${t.border};
  border-left:3px solid #F05A41;
  border-radius:14px; padding:14px 16px 18px;
  min-width:270px; max-width:340px;
  box-shadow:0 20px 50px rgba(0,0,0,0.35), 0 0 0 1px rgba(240,90,65,0.08);
  pointer-events:all; overflow:hidden; position:relative;
}
.export-toast.toast-enter { animation:toast-in 0.40s cubic-bezier(0.34,1.56,0.64,1) forwards; }
.export-toast.toast-leave { animation:toast-out 0.30s ease forwards; }
@keyframes toast-in {
  from { opacity:0; transform:translateX(90px) scale(0.88); }
  to   { opacity:1; transform:translateX(0)    scale(1); }
}
@keyframes toast-out {
  from { opacity:1; transform:translateX(0); max-height:100px; margin-bottom:0; }
  to   { opacity:0; transform:translateX(90px); max-height:0; margin-bottom:-10px; }
}
.toast-icon-wrap {
  width:38px; height:38px; border-radius:10px; flex-shrink:0;
  background:linear-gradient(135deg,#F05A41 0%,#D8A056 100%);
  display:flex; align-items:center; justify-content:center; font-size:18px;
  box-shadow:0 4px 14px rgba(240,90,65,0.35);
  animation:toast-icon-pop 0.45s cubic-bezier(0.34,1.56,0.64,1) 0.1s both;
}
@keyframes toast-icon-pop {
  from { transform:scale(0.5) rotate(-15deg); opacity:0; }
  to   { transform:scale(1)   rotate(0deg);   opacity:1; }
}
.toast-body { flex:1; min-width:0; }
.toast-title { font-size:13px; font-weight:700; color:${t.textPrimary}; }
.toast-sub   { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; margin-top:2px; }
.toast-check {
  color:#22c55e; font-size:17px; flex-shrink:0;
  animation:check-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;
}
@keyframes check-pop {
  from { transform:scale(0) rotate(-180deg); opacity:0; }
  to   { transform:scale(1) rotate(0deg);    opacity:1; }
}
.toast-progress-bar {
  position:absolute; bottom:0; left:0; height:3px;
  background:linear-gradient(90deg,#F05A41,#D8A056);
  animation:toast-shrink 3s linear 0.1s forwards;
  border-radius:0 4px 0 14px;
}
@keyframes toast-shrink {
  from { width:100%; }
  to   { width:0%; }
}

/* ── TRUST BANNER ── */
.trust-banner {
  display:flex; align-items:center; gap:8px;
  margin:0 1.5rem 0; padding:9px 16px;
  border-radius:10px;
  background:${isDark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.07)"};
  border:1px solid rgba(34,197,94,0.18);
  font-size:12px; font-family:'JetBrains Mono',monospace;
  color:${isDark ? "#86efac" : "#15803d"};
  letter-spacing:0.01em; margin-top:1rem;
}
.trust-banner-icon { font-size:14px; flex-shrink:0; }
.trust-banner-text { flex:1; line-height:1.4; }
.trust-banner-verify {
  font-size:10px; color:${isDark ? "#86efac" : "#15803d"};
  white-space:nowrap; text-decoration:underline; cursor:pointer;
  flex-shrink:0; background:none; border:none; padding:0; font-family:inherit;
}
.trust-banner-verify:hover { opacity:0.8; }
/* ── DEVTOOLS MODAL ── */
.devtools-backdrop {
  position:fixed; inset:0; z-index:10000;
  background:rgba(0,0,0,0.65);
  backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
  display:flex; align-items:center; justify-content:center;
  padding:20px;
  animation:backdrop-in 0.20s ease;
}
.devtools-card {
  background:${isDark ? "#0f1824" : "#fff"};
  border:1px solid ${t.border};
  border-radius:20px; width:100%; max-width:480px;
  box-shadow:0 32px 80px rgba(0,0,0,0.50);
  animation:contact-in 0.28s cubic-bezier(0.34,1.56,0.64,1);
  overflow:hidden; position:relative;
}
.devtools-card-header {
  background:linear-gradient(135deg,#0f1f30 0%,#162336 100%);
  border-bottom:1px solid ${t.border};
  padding:22px 24px 18px;
  display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
}
.devtools-card-title {
  font-size:17px; font-weight:700; color:${t.textPrimary};
  letter-spacing:-0.02em; display:flex; align-items:center; gap:9px;
}
.devtools-card-title span { font-size:20px; }
.devtools-card-sub {
  font-size:12px; color:${t.textMuted}; margin-top:4px;
}
.devtools-close {
  width:32px; height:32px; border-radius:8px; flex-shrink:0;
  background:none; border:1px solid ${t.border};
  color:${t.textMuted}; font-size:16px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:all 0.15s; line-height:1;
}
.devtools-close:hover { background:rgba(255,255,255,0.08); color:${t.textPrimary}; }
.devtools-card-body { padding:22px 24px 24px; }
.devtools-os-badge {
  display:inline-flex; align-items:center; gap:6px;
  background:${t.bgChipActive}; border:1px solid ${t.borderActive};
  border-radius:20px; padding:4px 12px; font-size:11px;
  color:${t.textAccent}; font-weight:600; margin-bottom:18px;
  font-family:'JetBrains Mono',monospace;
}
.devtools-steps { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:12px; }
.devtools-step {
  display:flex; align-items:flex-start; gap:12px;
  padding:12px 14px; border-radius:12px;
  background:${isDark ? "rgba(255,255,255,0.03)" : "#f8fafc"};
  border:1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"};
}
.devtools-step-num {
  width:24px; height:24px; border-radius:50%; flex-shrink:0;
  background:linear-gradient(135deg,#F05A41,#D8A056);
  color:#fff; font-size:11px; font-weight:700;
  display:flex; align-items:center; justify-content:center;
}
.devtools-step-content { flex:1; }
.devtools-step-label {
  font-size:13px; font-weight:600; color:${t.textPrimary}; margin-bottom:2px;
}
.devtools-step-desc { font-size:12px; color:${t.textMuted}; line-height:1.5; }
.devtools-kbd {
  display:inline-block;
  background:${isDark ? "#1a2535" : "#e2e8f0"};
  border:1px solid ${isDark ? "#2a3a50" : "#cbd5e1"};
  border-bottom-width:2px;
  border-radius:5px; padding:1px 7px;
  font-size:12px; font-family:'JetBrains Mono',monospace;
  color:${t.textPrimary}; white-space:nowrap;
}
.devtools-result {
  margin-top:16px; padding:12px 14px; border-radius:12px;
  background:rgba(74,222,128,0.08); border:1px solid rgba(74,222,128,0.25);
  display:flex; align-items:center; gap:10px;
}
.devtools-result-icon { font-size:18px; flex-shrink:0; }
.devtools-result-text { font-size:12px; color:#4ade80; font-weight:600; line-height:1.5; }
.devtools-result-text span { display:block; font-weight:400; color:${t.textMuted}; margin-top:2px; }
.devtools-console-note {
  margin-top:14px; padding:10px 14px; border-radius:10px;
  background:${isDark ? "rgba(248,197,85,0.06)" : "rgba(217,119,6,0.06)"};
  border:1px solid ${isDark ? "rgba(248,197,85,0.15)" : "rgba(217,119,6,0.15)"};
  font-size:11px; color:${isDark ? "#fbbf24" : "#92400e"}; line-height:1.5;
}
@media(max-width:600px) { .trust-banner { margin:1rem 1rem 0; } }

/* ── CONTACT MODAL ── */
.contact-backdrop {
  position:fixed; inset:0; z-index:10000;
  background:rgba(0,0,0,0.60);
  backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
  display:flex; align-items:center; justify-content:center;
  padding:20px;
  animation:backdrop-in 0.20s ease;
}
@keyframes backdrop-in { from{opacity:0} to{opacity:1} }
.contact-card {
  background:${isDark ? "#141a24" : "#fff"};
  border:1px solid ${t.border};
  border-radius:20px; padding:32px 28px 28px;
  width:100%; max-width:360px;
  box-shadow:0 32px 80px rgba(0,0,0,0.45);
  animation:contact-in 0.30s cubic-bezier(0.34,1.56,0.64,1);
  position:relative;
}
@keyframes contact-in {
  from { opacity:0; transform:scale(0.88) translateY(20px); }
  to   { opacity:1; transform:scale(1)    translateY(0); }
}
.contact-close {
  position:absolute; top:16px; right:16px;
  width:30px; height:30px; border-radius:8px;
  background:none; border:1px solid ${t.border};
  color:${t.textMuted}; font-size:16px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:all 0.15s; line-height:1;
}
.contact-close:hover { background:rgba(255,255,255,0.08); color:${t.textPrimary}; }
.contact-title {
  font-size:20px; font-weight:800; color:${t.textPrimary};
  letter-spacing:-0.03em; margin-bottom:6px;
}
.contact-sub {
  font-size:13px; color:${t.textMuted}; margin-bottom:24px; line-height:1.5;
}
.contact-btn {
  display:flex; align-items:center; gap:14px;
  width:100%; padding:16px 18px; border-radius:14px;
  border:1px solid ${t.border}; background:${t.bgPanel};
  cursor:pointer; text-decoration:none;
  transition:all 0.18s; margin-bottom:10px;
  font-family:'Plus Jakarta Sans',sans-serif;
}
.contact-btn:last-child { margin-bottom:0; }
.contact-btn:hover { transform:translateY(-2px); border-color:transparent; }
.contact-btn-email:hover  { background:linear-gradient(135deg,rgba(240,90,65,0.15),rgba(216,160,86,0.15)); border-color:rgba(240,90,65,0.35); }
.contact-btn-wa:hover    { background:rgba(37,211,102,0.10); border-color:rgba(37,211,102,0.35); }
.contact-btn-icon {
  width:42px; height:42px; border-radius:12px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:20px;
}
.contact-btn-icon-email { background:linear-gradient(135deg,#F05A41,#D8A056); box-shadow:0 4px 14px rgba(240,90,65,0.30); }
.contact-btn-icon-wa    { background:#25D366; box-shadow:0 4px 14px rgba(37,211,102,0.30); }
.contact-btn-label { font-size:14px; font-weight:700; color:${t.textPrimary}; margin-bottom:2px; }
.contact-btn-detail { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; }

/* ── EXPORT HISTORY PAGE ── */
.export-history-page { flex:1; padding:2rem 2.5rem 4rem; max-width:960px; margin:0 auto; width:100%; }
.export-history-stats {
  display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-bottom:32px;
}
.export-stat-card {
  background:${t.bgPanel}; border:1px solid ${t.border}; border-radius:14px;
  padding:20px 24px; position:relative; overflow:hidden;
}
.export-stat-card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:2px;
  background:linear-gradient(90deg,#F05A41,#D8A056);
}
.export-stat-value { font-size:32px; font-weight:800; color:${t.textPrimary}; letter-spacing:-0.03em; margin-bottom:4px; }
.export-stat-label { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; }
.export-history-filters { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:24px; }
.export-filter-chip {
  padding:6px 14px; border-radius:20px; font-size:12px; font-weight:600;
  border:1px solid ${t.border}; background:${t.bgPanel};
  color:${t.textSecondary}; cursor:pointer; transition:all 0.15s;
  font-family:'JetBrains Mono',monospace;
}
.export-filter-chip:hover { border-color:rgba(240,90,65,0.4); color:${t.textAccent}; }
.export-filter-chip.active {
  background:linear-gradient(135deg,#F05A41 0%,#D8A056 100%);
  border-color:transparent; color:#fff;
}
.export-history-list { display:flex; flex-direction:column; gap:10px; }
.export-history-item {
  display:flex; align-items:center; gap:14px;
  background:${t.bgPanel}; border:1px solid ${t.border};
  border-radius:12px; padding:14px 18px;
  transition:border-color 0.15s, transform 0.15s;
  animation:item-slide-in 0.3s ease both;
  text-decoration:none;
}
.export-history-item:hover { border-color:rgba(240,90,65,0.25); transform:translateX(3px); }
@keyframes item-slide-in {
  from { opacity:0; transform:translateY(10px); }
  to   { opacity:1; transform:translateY(0); }
}
.export-history-tool-icon {
  width:40px; height:40px; border-radius:10px; flex-shrink:0;
  background:linear-gradient(135deg,rgba(240,90,65,0.12) 0%,rgba(216,160,86,0.12) 100%);
  display:flex; align-items:center; justify-content:center; font-size:18px;
}
.export-history-meta { flex:1; min-width:0; }
.export-history-tool-name { font-size:14px; font-weight:700; color:${t.textPrimary}; margin-bottom:3px; }
.export-history-detail { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; }
.export-history-rows {
  font-size:12px; font-weight:700; color:${t.textAccent};
  font-family:'JetBrains Mono',monospace;
  background:rgba(240,90,65,0.08); border-radius:8px; padding:4px 10px;
  flex-shrink:0;
}
.export-history-time { font-size:11px; color:${t.textMuted}; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
.export-history-go { font-size:18px; color:${t.textMuted}; flex-shrink:0; transition:color 0.15s; }
.export-history-item:hover .export-history-go { color:#ffb4a6; }
.export-history-date-divider {
  display:flex; align-items:center; gap:12px; margin:20px 0 12px;
}
.export-history-date-label {
  font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.14em;
  color:${t.textMuted}; font-family:'JetBrains Mono',monospace; white-space:nowrap;
}
.export-history-date-line { flex:1; height:1px; background:${t.border}; }
.export-empty-state {
  text-align:center; padding:80px 20px;
  background:${t.bgPanel}; border:1px dashed ${t.border}; border-radius:16px;
}
.export-empty-icon { font-size:56px; margin-bottom:16px; opacity:0.5; }
.export-empty-title { font-size:18px; font-weight:700; color:${t.textPrimary}; margin-bottom:8px; }
.export-empty-sub { font-size:13px; color:${t.textMuted}; }
@media(max-width:700px) {
  .export-history-page { padding:1.5rem 1rem 3rem; }
  .export-history-stats { grid-template-columns:1fr 1fr; }
  .export-history-rows { display:none; }
}
@media(max-width:400px) {
  .export-history-stats { grid-template-columns:1fr; }
}
`;
}

// ─── SIDEBAR ─────────────────────────────────────────────────────────────────
function Sidebar({ t, sidebarOpen, setSidebarOpen, mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const closeIfMobile = useCallback(() => {
    if (window.innerWidth <= 768) setMobileOpen(false);
  }, [setMobileOpen]);

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}
        role="navigation" aria-label="Tool navigation">

        <div className="sb-header">
          <Link to="/" className="sb-logo-icon" onClick={closeIfMobile} title="miniDevToolkit — Home" aria-label="Home">
            <LogoMark size={36} />
          </Link>
          <div className="sb-brand">
            <div className="sb-brand-name">miniDevToolkit</div>
            <div className="sb-brand-sub">V1.0.4 Precision Tooling</div>
          </div>
          <button className="sb-toggle" onClick={() => setSidebarOpen(v => !v)}
            title="Collapse sidebar" aria-label="Collapse sidebar">‹</button>
        </div>

        {/* "+ New Tool" button */}
        {/*
          <Link to="/" className="sb-new-tool-btn" onClick={closeIfMobile} aria-label="All tools">
          <span className="sb-ntb-icon">＋</span>
          <span className="sb-ntb-label">New Tool</span>
        </Link>
        */}

        <div className="sb-body">
          <Link to="/"
            className={`sb-nav-item sb-home-item ${currentPath === "/" ? "active" : ""}`}
            onClick={closeIfMobile}>
            <div className="sb-icon-cell">
              <div className="sb-icon-box"><HomeNavIcon size={20} /></div>
            </div>
            <div className="sb-label-cell">
              <div className="sb-tool-name">Home</div>
            </div>
            <span className="sb-tooltip">Home</span>
          </Link>

          <div className="sb-divider" />

          {TOOL_GROUPS.map(grp => (
            <div key={grp.group}>
              <div className="sb-group-label">{grp.group}</div>
              {grp.tools.map(tool => {
                const isActive = currentPath === tool.path;
                return (
                  <Link key={tool.id} to={tool.path}
                    className={`sb-nav-item ${isActive ? "active" : ""}`}
                    onClick={closeIfMobile}
                    aria-label={tool.name}
                    aria-current={isActive ? "page" : undefined}>
                    <div className="sb-icon-cell">
                      <div className="sb-icon-box"><ToolIcon id={tool.id} size={30} /></div>
                    </div>
                    <div className="sb-label-cell">
                      <div className="sb-tool-name">{tool.name}</div>
                    </div>
                    <span className="sb-tooltip">{tool.name}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* Bottom section: Docs, Support, FAQ */}
        <div className="sb-bottom">
          <Link to="/exports"
            className={`sb-nav-item ${currentPath === "/exports" ? "active" : ""}`}
            onClick={closeIfMobile}>
            <div className="sb-icon-cell">
              <div className="sb-icon-box"><ExportsNavIcon size={20} /></div>
            </div>
            <div className="sb-label-cell">
              <div className="sb-tool-name">Export History</div>
            </div>
            <span className="sb-tooltip">Export History</span>
          </Link>
          <Link to="/privacy"
            className={`sb-nav-item ${currentPath === "/privacy" ? "active" : ""}`}
            onClick={closeIfMobile}>
            <div className="sb-icon-cell">
              <div className="sb-icon-box"><PrivacyNavIcon size={20} /></div>
            </div>
            <div className="sb-label-cell">
              <div className="sb-tool-name">Privacy Policy</div>
            </div>
            <span className="sb-tooltip">Privacy</span>
          </Link>
          <Link to="/faq"
            className={`sb-nav-item ${currentPath === "/faq" ? "active" : ""}`}
            onClick={closeIfMobile}>
            <div className="sb-icon-cell">
              <div className="sb-icon-box"><FaqNavIcon size={20} /></div>
            </div>
            <div className="sb-label-cell">
              <div className="sb-tool-name">Support / FAQ</div>
            </div>
            <span className="sb-tooltip">FAQ</span>
          </Link>
          <a href="https://aqibjaved.vercel.app/" target="_blank" rel="noopener noreferrer"
            className="sb-nav-item">
            <div className="sb-icon-cell">
              <div className="sb-icon-box"><PortfolioNavIcon size={20} /></div>
            </div>
            <div className="sb-label-cell">
              <div className="sb-tool-name">Portfolio</div>
            </div>
            <span className="sb-tooltip">Portfolio</span>
          </a>
        </div>

      </aside>
    </>
  );
}

// ─── FEATURE STRIP DATA ───────────────────────────────────────────────────────
const FEATURES = [
  { Icon: FeaturePrivacyIcon,  title: "100% Private",  desc: "Everything stays in your browser. We never see your data." },
  { Icon: FeatureInstantIcon,  title: "Instant",       desc: "Blazing fast client-side processing. No waiting, no spinners." },
  { Icon: FeatureNoSignupIcon, title: "No Sign-up",    desc: "Just open and use. No credit cards, no emails, no friction." },
  { Icon: FeatureOfflineIcon,  title: "Works Offline", desc: "Fully responsive PWA. Works once loaded, no internet needed." },
];

// ─── FAQ TEASER DATA ──────────────────────────────────────────────────────────
const FAQ_TEASER = [
  {
    q: "Is my data safe when using miniDevToolkit?",
    a: "Yes. All tools run entirely in your browser — your JSON, files, passwords, and text are never sent to any server.",
  },
  {
    q: "Do I need to create an account?",
    a: "No account or sign-up is required. Open the site and start using any tool immediately.",
  },
  {
    q: "How many tools does miniDevToolkit have?",
    a: "25+ individual tools across 9 toolkits: JSON, Encoding, Generators, Fake Data, Hash, File Converter, Time & Date, Text, and Compression.",
  },
];

// ─── SCROLL ANIMATION HOOK ────────────────────────────────────────────────────
function useRevealOnScroll(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add("fade-up");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.10 }
    );
    els.forEach((el, i) => {
      el.style.opacity = "0";
      el.style.animationDelay = `${i * 0.05}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ref]);
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ t, exports }) {
  const navigate  = useNavigate();
  const gridRef   = useRef(null);
  const toolsRef  = useRef(null);
  useRevealOnScroll(gridRef);

  const scrollToTools = () => toolsRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <Helmet>
        <title>MiniDevToolkit — 25+ Free Online Developer Tools</title>
        <meta name="description" content="Free browser-based developer tools: JSON formatter, Base64/JWT encoder, MD5/SHA-256 hash, UUID, regex tester, text diff & more. No sign-up. 100% private." />
        <link rel="canonical" href="https://minidevtoolkit.com/" />
      </Helmet>

      <div style={{ flex: 1 }}>

        {/* ── HERO ── */}
        <section className="stitch-hero" aria-label="Hero">
          <div className="stitch-hero-blob1" />
          <div className="stitch-hero-blob2" />
          <div className="stitch-hero-inner">
            <h2 className="fade-up">
              Developer tools that never<br />
              <em>leave your browser.</em>
            </h2>
            <p className="stitch-hero-sub fade-up d1">
              25+ privacy-first tools. No uploads. No sign-up. No tracking.
              Precision-engineered for modern workflows.
            </p>
            <div className="stitch-hero-ctas fade-up d2">
              <button className="btn-primary" onClick={scrollToTools}
                style={{ padding: "13px 28px", fontSize: 15 }}>
                Explore tools →
              </button>
              <Link to="/faq" className="btn-ghost"
                style={{ padding: "13px 28px", fontSize: 15, textDecoration: "none" }}>
                Read the FAQ
              </Link>
            </div>
            <div className="stitch-trust fade-up d3">
              <span className="stitch-trust-label">Trusted by builders at:</span>
              <div className="stitch-trust-badges">
                <span className="stitch-trust-badge">OPEN_SOURCE</span>
                <span className="stitch-trust-badge">DEV_CORP</span>
                <span className="stitch-trust-badge">TECH_NODE</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── FEATURE STRIP ── */}
        <section className="stitch-features" aria-label="Key features">
          <div className="stitch-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="stitch-feature-card fade-up" style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="stitch-feature-icon"><f.Icon size={24} /></div>
                <div className="stitch-feature-title">{f.title}</div>
                <div className="stitch-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TOOLS GRID ── */}
        <section className="stitch-tools-section" ref={toolsRef} aria-label="All tools">
          <div className="stitch-tools-category-label">ALL TOOLS</div>
          <div ref={gridRef}>
            {TOOL_GROUPS.map((grp) => {
              return (
                <div key={grp.group} style={{ marginBottom: 40 }}>
                  <div className="stitch-tools-header">
                    <div>
                      <div className="stitch-tools-category-title">{grp.group}</div>
                    </div>
                  </div>
                  <div className="stitch-tools-grid">
                    {grp.tools.map((tool) => {
                      return (
                        <div key={tool.id} className="stitch-tool-card" data-reveal
                          onClick={() => navigate(tool.path)}
                          role="button" tabIndex={0}
                          onKeyDown={e => e.key === "Enter" && navigate(tool.path)}
                          aria-label={`Open ${tool.name}`}>
                          <div className="stitch-tool-card-left">
                            <div className="stitch-tool-icon-box">
                              <ToolIcon id={tool.id} size={40} />
                            </div>
                            <div>
                              <div className="stitch-tool-name">{tool.name}</div>
                              <div className="stitch-tool-desc">{tool.desc}</div>
                            </div>
                          </div>
                          <span className="stitch-tool-chevron">›</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Coming soon */}
            <div style={{ marginBottom: 40 }}>
              <div className="stitch-tools-header">
                <div>
                  <div className="stitch-tools-category-label">COMING SOON</div>
                  <div className="stitch-tools-category-title" style={{ fontSize: 22, opacity: 0.5 }}>More Tools</div>
                </div>
              </div>
              <div className="stitch-tools-grid">
                <div className="stitch-tool-card" style={{ opacity: 0.4, cursor: "default", pointerEvents: "none" }}>
                  <div className="stitch-tool-card-left">
                    <div className="stitch-tool-icon-box" style={{ background: "rgba(100,116,139,0.10)", color: "#64748b" }}>🔜</div>
                    <div>
                      <div className="stitch-tool-name">More Tools</div>
                      <div className="stitch-tool-desc">Color picker, number base converter & more</div>
                    </div>
                  </div>
                  <span className="stitch-tool-chevron">›</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── RECENT EXPORTS + PRO TIP ── */}
        <div className="stitch-bottom-grid">
          {/* Exports */}
          <div className="stitch-exports-card">
            <div className="stitch-exports-header">
              <span className="stitch-exports-title">Recent Exports</span>
              <span className="stitch-exports-time">This Session</span>
            </div>
            {exports.length === 0 ? (
              <div className="stitch-export-empty">
                <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
                No exports yet — use any tool to see activity here.
              </div>
            ) : (
              <table className="stitch-export-table">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>Rows</th>
                    <th>Time</th>
                    <th style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {exports.slice(0, 8).map((ex, i) => {
                    const tool = ALL_TOOLS.find(t2 => t2.name.startsWith(ex.tool?.split("→")[0]?.trim()));
                    return (
                      <tr key={i}>
                        <td className="col-tool">
                          <span style={{ marginRight: 8 }}>{tool?.icon ?? "📦"}</span>
                          {ex.tool?.toUpperCase().replace(/ /g, "_").slice(0, 20)}
                        </td>
                        <td>{(ex.rows || 0).toLocaleString()}</td>
                        <td>{formatTime(ex.timestamp)}</td>
                        <td style={{ textAlign: "right" }}>
                          <button style={{ background: "none", border: "none", cursor: "pointer", color: "#ffb4a6", fontSize: 14 }}
                            title="Export logged">↓</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Right side cards */}
          <div className="stitch-side-cards">
            <div className="pro-tip-card">
              <div className="pro-tip-title">
                <span style={{ color: "#f7bc6f" }}>⚡</span> Pro Tip
              </div>
              <p>
                Press <kbd>Ctrl+K</kbd> or use the search bar above to instantly jump between tools.
                All processing stays in your browser — no data ever sent to servers.
              </p>
            </div>

            <div className="pro-tip-card" style={{ background: t.bgPanel, position: "relative", overflow: "hidden" }}>
              <div style={{
                position: "absolute", right: -16, bottom: -16,
                width: 80, height: 80, borderRadius: "50%",
                background: "rgba(240,90,65,0.08)", filter: "blur(20px)"
              }} />
              <div className="pro-tip-title">
                <span style={{ color: "#ffb4a6" }}>🐳</span> Self-Hostable
              </div>
              <p style={{ marginBottom: 12 }}>
                Want to run miniDevToolkit on your local network? Deploy it anywhere.
              </p>
              <a href="https://aqibjaved.vercel.app/" target="_blank" rel="noopener noreferrer"
                style={{ color: "#f7bc6f", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                View Portfolio →
              </a>
            </div>

            {/* FAQ Teaser */}
            <div className="pro-tip-card" style={{ background: "transparent", border: "none", padding: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: t.textMuted, marginBottom: 10, fontFamily: "JetBrains Mono" }}>Quick FAQ</div>
              {FAQ_TEASER.slice(0, 2).map((item, i) => (
                <Link key={i} to="/faq" className="faq-teaser-item">
                  <div className="faq-teaser-q">{item.q}</div>
                </Link>
              ))}
              <Link to="/faq" style={{ display: "block", textAlign: "center", marginTop: 8, color: t.textAccent, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                View all FAQ →
              </Link>
            </div>
          </div>
        </div>

        {/* ── ALTERNATIVES SECTION ── */}
        <section style={{ padding: "0 3rem 2rem" }} aria-label="Alternatives">
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: t.textMuted, fontFamily: "JetBrains Mono", marginBottom: 8 }}>
            Looking for alternatives?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
            {[
              { to: "/alternatives/jsonformatter", label: "Free alternative to JSONFormatter.org", desc: "JSON diff, CSV/XML/YAML export, offline" },
              { to: "/alternatives/cyberchef",    label: "Lightweight alternative to CyberChef", desc: "Simpler UI for everyday encoding tasks" },
              { to: "/alternatives/dcode",        label: "Clean alternative to dcode.fr",        desc: "Encoding + hashing + JSON in one tab" },
            ].map(card => (
              <Link key={card.to} to={card.to}
                style={{ background: t.bgPanel, border: `1px solid ${t.border}`, borderRadius: 12, padding: "14px 16px", textDecoration: "none", transition: "border-color 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(240,90,65,0.30)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 4 }}>{card.label}</div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "JetBrains Mono" }}>{card.desc}</div>
              </Link>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}

// ─── TOOL PAGE WRAPPER ────────────────────────────────────────────────────────
function ToolPage({ toolId, t, onExport }) {
  const tool = ALL_TOOLS.find(x => x.id === toolId);
  const [showVerifyTip, setShowVerifyTip] = useState(false);

  // Close modal on Escape — must be before early return
  useEffect(() => {
    if (!showVerifyTip) return;
    const handler = (e) => { if (e.key === "Escape") setShowVerifyTip(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showVerifyTip]);

  const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
  const devtoolsShortcut = isMac ? "⌘ ⌥ I" : "F12";

  const handleVerifyClick = () => {
    setShowVerifyTip(v => !v);
    /* eslint-disable no-console */
    console.group(
      '%c 🔒  miniDevToolkit — Privacy Verification',
      'background:#0a141f;color:#F05A41;font-weight:700;font-size:14px;padding:4px 10px;border-radius:4px;border-left:3px solid #F05A41'
    );
    console.log(
      '%c ℹ  How to verify',
      'color:#D8A056;font-weight:600;font-size:12px'
    );
    console.log(
      `%c  1.  Press ${devtoolsShortcut} to open DevTools (you're already here!)`,
      'color:#94a3b8;font-size:12px'
    );
    console.log(
      '%c  2.  Click the  Network  tab at the top of this panel',
      'color:#94a3b8;font-size:12px'
    );
    console.log(
      '%c  3.  Use any tool — paste JSON, hash text, generate a UUID, compress an image…',
      'color:#94a3b8;font-size:12px'
    );
    console.log(
      '%c  4.  Look at the requests list ↓',
      'color:#94a3b8;font-size:12px'
    );
    console.log(
      '%c ✅  ZERO requests to any external server containing your data.',
      'background:#052e16;color:#4ade80;font-weight:700;font-size:13px;padding:3px 8px;border-radius:4px'
    );
    console.log(
      '%c ⚙  Everything runs 100% inside this browser tab via JavaScript — no uploads, no servers.',
      'color:#64748b;font-size:11px'
    );
    console.groupEnd();
    /* eslint-enable no-console */
  };

  if (!tool) return null;

  return (
    <>
      <Helmet>
        <title>{tool.title}</title>
        <meta name="description" content={tool.meta} />
        {tool.keywords && <meta name="keywords" content={tool.keywords} />}
        <link rel="canonical" href={`https://minidevtoolkit.com${tool.path}`} />
        <meta property="og:title"       content={tool.title} />
        <meta property="og:description" content={tool.meta} />
        <meta property="og:url"         content={`https://minidevtoolkit.com${tool.path}`} />
        <meta property="og:type"        content="website" />
        <meta property="og:image"       content="https://minidevtoolkit.com/og-image.png" />
        <meta name="twitter:title"       content={tool.title} />
        <meta name="twitter:description" content={tool.meta} />
        <meta name="twitter:image"       content="https://minidevtoolkit.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org",
          "@type":"BreadcrumbList",
          "itemListElement":[
            {"@type":"ListItem","position":1,"name":"Home","item":"https://minidevtoolkit.com/"},
            {"@type":"ListItem","position":2,"name":tool.name,"item":`https://minidevtoolkit.com${tool.path}`},
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org",
          "@type":"SoftwareApplication",
          "name": tool.name,
          "url": `https://minidevtoolkit.com${tool.path}`,
          "description": tool.meta,
          "applicationCategory": "DeveloperApplication",
          "applicationSubCategory": "Utility",
          "operatingSystem": "Any (Web Browser)",
          "browserRequirements": "Requires JavaScript. Works in Chrome, Firefox, Safari, Edge.",
          "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
          "isPartOf": { "@type":"WebApplication", "name":"miniDevToolkit", "url":"https://minidevtoolkit.com/" },
          "publisher": { "@type":"Person", "name":"Aqib", "url":"https://aqibjaved.vercel.app/" },
        })}</script>
        {tool.faqs && tool.faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context":"https://schema.org",
            "@type":"FAQPage",
            "mainEntity": tool.faqs.map(f => ({
              "@type":"Question",
              "name": f.q,
              "acceptedAnswer": { "@type":"Answer", "text": f.a },
            })),
          })}</script>
        )}
      </Helmet>
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Tool header badge + trust banner */}
        <h2 style={{ position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden", clip:"rect(0,0,0,0)", whiteSpace:"nowrap", border:0 }}>{tool.name} — Free Online Tool</h2>
        <div style={{ padding: "1.25rem 1.5rem 0", display: "flex", alignItems: "center", gap: 10 }}>
          <span className="page-badge">
            <span>{tool.icon}</span>
            <span>{tool.name.toUpperCase().replace(/ /g, "_")} MODULE</span>
          </span>
        </div>
        <div className="trust-banner" role="status" aria-label="Privacy notice">
          <span className="trust-banner-icon">🔒</span>
          <span className="trust-banner-text">
            100% browser-based — your data never leaves your device. No uploads, no servers.
          </span>
          <button className="trust-banner-verify" onClick={handleVerifyClick}
            aria-label="How to verify privacy in DevTools">
            Verify in DevTools ↗
          </button>
        </div>
        <div style={{ flex: 1 }}>
          {TOOL_COMPONENTS[toolId]?.(t, onExport)}
        </div>
        <RelatedTools toolId={toolId} t={t} />
      </div>

      {/* DevTools verification modal */}
      {showVerifyTip && (
        <div className="devtools-backdrop" onClick={() => setShowVerifyTip(false)}
          role="dialog" aria-modal="true" aria-label="How to verify privacy">
          <div className="devtools-card" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="devtools-card-header">
              <div>
                <div className="devtools-card-title">
                  <span>🔒</span> Verify Privacy in DevTools
                </div>
                <div className="devtools-card-sub">
                  Confirm your data never leaves your browser — takes 30 seconds
                </div>
              </div>
              <button className="devtools-close" onClick={() => setShowVerifyTip(false)} aria-label="Close">✕</button>
            </div>

            {/* Body */}
            <div className="devtools-card-body">
              <div className="devtools-os-badge">
                {isMac ? "🍎 Mac" : "🪟 Windows / Linux"} — shortcut: <strong>{devtoolsShortcut}</strong>
              </div>

              <ol className="devtools-steps">
                <li className="devtools-step">
                  <div className="devtools-step-num">1</div>
                  <div className="devtools-step-content">
                    <div className="devtools-step-label">Open DevTools</div>
                    <div className="devtools-step-desc">
                      Press <kbd className="devtools-kbd">{devtoolsShortcut}</kbd> on your keyboard,
                      or right-click anywhere on the page and choose <strong>Inspect</strong>.
                    </div>
                  </div>
                </li>
                <li className="devtools-step">
                  <div className="devtools-step-num">2</div>
                  <div className="devtools-step-content">
                    <div className="devtools-step-label">Go to the Network tab</div>
                    <div className="devtools-step-desc">
                      Click <kbd className="devtools-kbd">Network</kbd> in the top tab bar of DevTools.
                      If you don't see it, look for a <strong>&rsaquo;&rsaquo;</strong> overflow menu.
                    </div>
                  </div>
                </li>
                <li className="devtools-step">
                  <div className="devtools-step-num">3</div>
                  <div className="devtools-step-content">
                    <div className="devtools-step-label">Use any tool</div>
                    <div className="devtools-step-desc">
                      Paste some JSON, hash a password, generate a UUID — anything that processes your data.
                    </div>
                  </div>
                </li>
                <li className="devtools-step">
                  <div className="devtools-step-num">4</div>
                  <div className="devtools-step-content">
                    <div className="devtools-step-label">Check the requests list</div>
                    <div className="devtools-step-desc">
                      Watch the Network tab. You will see requests for fonts and analytics — but <strong>zero</strong> requests containing your actual data.
                    </div>
                  </div>
                </li>
              </ol>

              <div className="devtools-result">
                <div className="devtools-result-icon">✅</div>
                <div className="devtools-result-text">
                  Your data never leaves this browser tab
                  <span>All processing runs 100% in JavaScript locally — no uploads, no servers.</span>
                </div>
              </div>

              <div className="devtools-console-note">
                💡 A colour-formatted guide has also been printed to the <strong>Console</strong> tab for reference.
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

// ─── 404 PAGE ─────────────────────────────────────────────────────────────────
function NotFoundPage({ t }) {
  const navigate = useNavigate();
  return (
    <>
      <Helmet><title>404 — Page Not Found | MiniDevToolkit</title></Helmet>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 2rem", textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔧</div>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: t.textPrimary, marginBottom: 10, letterSpacing: "-0.03em" }}>404</h1>
        <p style={{ fontSize: 15, color: t.textSecondary, marginBottom: 24 }}>This page doesn't exist. Try one of the tools below.</p>
        <button className="btn-primary" onClick={() => navigate("/")} style={{ padding: "10px 24px", fontSize: 14 }}>← Back to Home</button>
      </div>
    </>
  );
}

// ─── CONTACT MODAL ───────────────────────────────────────────────────────────
function ContactModal({ onClose }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="contact-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Contact">
      <div className="contact-card" onClick={e => e.stopPropagation()}>
        <button className="contact-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="contact-title">Get in touch</div>
        <div className="contact-sub">Pick your preferred way to reach out.</div>

        {/* Email */}
        <a
          href="mailto:aqibjaved520@gmail.com"
          className="contact-btn contact-btn-email"
          onClick={onClose}
        >
          <div className="contact-btn-icon contact-btn-icon-email">✉</div>
          <div>
            <div className="contact-btn-label">Send an Email</div>
            <div className="contact-btn-detail">aqibjaved520@gmail.com</div>
          </div>
        </a>

        {/* WhatsApp */}
        <a
          href="https://wa.me/923035206753"
          target="_blank"
          rel="noopener noreferrer"
          className="contact-btn contact-btn-wa"
          onClick={onClose}
        >
          <div className="contact-btn-icon contact-btn-icon-wa">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <div>
            <div className="contact-btn-label">Message on WhatsApp</div>
            <div className="contact-btn-detail">+92 303 520 6753</div>
          </div>
        </a>
      </div>
    </div>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
function Footer({ t }) {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <footer style={{ background: t.bgFooter, borderTop: `1px solid ${t.border}`, padding: "3rem 3rem 1.5rem", marginTop: "auto" }} role="contentinfo">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="footer-grid">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <LogoMark size={28} />
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.03em",
                background: "linear-gradient(135deg,#F05A41,#D8A056)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                miniDevToolkit
              </span>
            </div>
            <p style={{ fontSize: 13, color: t.footerText, lineHeight: 1.7, marginBottom: 12, maxWidth: 260 }}>
              A minimalist collection of essential tools for software engineers. Privacy first, always.
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["Free", "No sign-up", "No uploads", "Private"].map(tag => (
                <span key={tag} style={{ background: t.tagBg, border: `1px solid ${t.tagBorder}`, borderRadius: 20, padding: "3px 10px", fontSize: 10, fontFamily: "JetBrains Mono", color: t.tagText }}>{tag}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: t.textMuted, marginBottom: 14, fontFamily: "JetBrains Mono" }}>All Tools</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ALL_TOOLS.map(tool => (
                <Link key={tool.id} to={tool.path}
                  style={{ fontSize: 13, color: t.footerLink, textDecoration: "none", transition: "color 0.15s" }}
                  onMouseEnter={e => e.target.style.color = t.footerLinkHover}
                  onMouseLeave={e => e.target.style.color = t.footerLink}>
                  {tool.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: t.textMuted, marginBottom: 14, fontFamily: "JetBrains Mono" }}>Resources</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/free-developer-tools" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>All Free Tools Hub</Link>
              <Link to="/blog/best-free-dev-tools-2026" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>Best Dev Tools 2026</Link>
              <Link to="/faq" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>Support & FAQ</Link>
              <Link to="/privacy" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>Privacy Policy</Link>
              <a href="https://aqibjaved.vercel.app/" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>Portfolio</a>
              <button
                onClick={() => setContactOpen(true)}
                style={{ fontSize: 13, color: t.footerLink, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "color 0.15s" }}
                onMouseEnter={e => e.target.style.color = t.footerLinkHover}
                onMouseLeave={e => e.target.style.color = t.footerLink}
              >Contact</button>
              {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
              <a href="https://github.com/aqibjaved520" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>GitHub ↗</a>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: t.textMuted, marginBottom: 14, fontFamily: "JetBrains Mono" }}>Alternatives</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/alternatives/jsonformatter" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>vs JSONFormatter.org</Link>
              <Link to="/alternatives/cyberchef" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>vs CyberChef</Link>
              <Link to="/alternatives/dcode" style={{ fontSize: 13, color: t.footerLink, textDecoration: "none" }}>vs dcode.fr</Link>
            </div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${t.footerBorder}`, paddingTop: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 11, color: t.footerText, fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            © {new Date().getFullYear()} Precision Tooling Lab. All rights reserved.
          </span>
          {/*
          <a href="https://aqibjaved.vercel.app/" target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: t.footerLink, textDecoration: "none", fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Built by Aqib Javed ↗
          </a>
          */}
        </div>
      </div>
    </footer>
  );
}

// ─── APP LAYOUT ──────────────────────────────────────────────────────────────
function AppLayout({ t, sidebarOpen, setSidebarOpen, mobileOpen, setMobileOpen, exports, onExport, onClearExports }) {
  const location   = useLocation();
  const navigate   = useNavigate();
  const [searchQ, setSearchQ]       = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const currentTool = ALL_TOOLS.find(tool => tool.path === location.pathname);

  // Filtered search results
  const filteredTools = searchQ.length > 1
    ? ALL_TOOLS.filter(tl =>
        tl.name.toLowerCase().includes(searchQ.toLowerCase()) ||
        (tl.desc && tl.desc.toLowerCase().includes(searchQ.toLowerCase()))
      ).slice(0, 6)
    : [];

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // Body scroll lock on mobile
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Close search on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
        searchRef.current?.querySelector("input")?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close search on resize
  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [setMobileOpen]);

  // Determine active quick link
  const isJsonPath = location.pathname === "/json-toolkit";
  const isConverterPath = location.pathname === "/file-converter";
  const isEncodingPath = location.pathname === "/encoding-toolkit";

  return (
    <div className="app-shell">
      <Sidebar
        t={t}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className="main-content">
        {/* ── TOP BAR ── */}
        <header className="top-bar" role="banner">
          <div className="top-bar-left">
            {/* Mobile hamburger */}
            <button
              className={`hamburger-btn ${mobileOpen ? "open" : ""}`}
              onClick={() => setMobileOpen(v => !v)}
              aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={mobileOpen}
              style={{ display: "none" }}
              id="mobile-hamburger"
            >
              <span/><span/><span/>
            </button>

            {/* Desktop expand button */}
            {!sidebarOpen && (
              <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}
                aria-label="Expand sidebar" style={{ display: "flex" }}>
                <span/><span/><span/>
              </button>
            )}

            {/* Search */}
            <div className="top-bar-search-wrap" ref={searchRef}>
              <span className="top-bar-search-icon">⌕</span>
              <input
                className="top-bar-search"
                placeholder="Search tools…"
                value={searchQ}
                onChange={e => { setSearchQ(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                aria-label="Search tools"
                aria-autocomplete="list"
              />
              {searchOpen && filteredTools.length > 0 && (
                <div className="search-dropdown" role="listbox">
                  {filteredTools.map(tl => (
                    <div key={tl.id} className="search-dropdown-item"
                      role="option" aria-selected="false"
                      onClick={() => { navigate(tl.path); setSearchQ(""); setSearchOpen(false); }}>
                      <span className="search-dropdown-item-icon">{tl.icon}</span>
                      <div>
                        <div className="search-dropdown-item-name">{tl.name}</div>
                        <div className="search-dropdown-item-desc">{tl.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick links */}
            <nav className="top-bar-quicknav" aria-label="Quick links">
              <Link to="/json-toolkit" className={`top-bar-qlink ${isJsonPath ? "qlink-active" : ""}`}>Formatter</Link>
              <Link to="/json-toolkit" className={`top-bar-qlink ${isJsonPath ? "qlink-active" : ""}`}>Validator</Link>
              <Link to="/encoding-toolkit" className={`top-bar-qlink ${isEncodingPath ? "qlink-active" : ""}`}>Encoder</Link>
              <Link to="/file-converter" className={`top-bar-qlink ${isConverterPath ? "qlink-active" : ""}`}>Converter</Link>
            </nav>
          </div>

          <div className="top-bar-right">
            {/*
            <button className="top-bar-icon-btn" title="Notifications" aria-label="Notifications">
              🔔
            </button>
            <button className="top-bar-icon-btn" title="Terminal" aria-label="Terminal">
              ⌨
            </button>
            */}
            <div className="top-bar-sep" />
            <button className="top-bar-export-btn" onClick={() => navigate("/exports")}
              title="View export history">
              Export {exports.length > 0 ? `(${exports.length})` : ""}
            </button>
            <div className="top-bar-avatar" title="Built by Aqib Javed" aria-label="User">AJ</div>
          </div>
        </header>

        <style>{`@media(max-width:768px){#mobile-hamburger{display:flex!important}}`}</style>

        <main id="main-content" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Routes>
            <Route path="/"                    element={<HomePage t={t} exports={exports} />} />
            <Route path="/json-toolkit"        element={<ToolPage toolId="json-toolkit"    t={t} onExport={onExport} />} />
            <Route path="/encoding-toolkit"    element={<ToolPage toolId="encoding"        t={t} onExport={onExport} />} />
            <Route path="/generators"          element={<ToolPage toolId="generators"      t={t} onExport={onExport} />} />
            <Route path="/fake-data-generator" element={<ToolPage toolId="fake-data"       t={t} onExport={onExport} />} />
            <Route path="/hash-generator"      element={<ToolPage toolId="hash-tools"      t={t} onExport={onExport} />} />
            <Route path="/file-converter"      element={<ToolPage toolId="file-converter"  t={t} onExport={onExport} />} />
            <Route path="/time-date-tools"     element={<ToolPage toolId="time-tools"      t={t} onExport={onExport} />} />
            <Route path="/text-utilities"      element={<ToolPage toolId="text-tools"      t={t} onExport={onExport} />} />
            <Route path="/compression-toolkit" element={<ToolPage toolId="compression"     t={t} onExport={onExport} />} />
            <Route path="/exports"             element={<ExportHistoryPage t={t} exports={exports} onClear={onClearExports} />} />
            <Route path="/faq"                 element={<FaqPage t={t} />} />
            <Route path="/privacy"             element={<PrivacyPage t={t} />} />
            <Route path="/free-developer-tools"                  element={<FreeDeveloperTools t={t} />} />
            <Route path="/alternatives/jsonformatter"            element={<JsonFormatterAlternative t={t} />} />
            <Route path="/alternatives/cyberchef"               element={<CyberChefAlternative t={t} />} />
            <Route path="/alternatives/dcode"                   element={<DcodeAlternative t={t} />} />
            <Route path="/blog/best-free-dev-tools-2026"        element={<BestFreeDevTools2026 t={t} />} />
            <Route path="*"                    element={<NotFoundPage t={t} />} />
          </Routes>
        </main>

        <Footer t={t} />
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [mode, setMode]               = useState(() => lsGet("mdt-theme", "dark"));
  const [sidebarOpen, setSidebarOpen] = useState(() => lsGet("mdt-sidebar", true));
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [exports, setExports]         = useState(() => lsGet("mdt-exports", []));
  const [toasts, setToasts]           = useState([]);

  const t   = THEMES[mode];
  const css = FONTS + makeBaseStyles(t) + makeSidebarStyles(t, sidebarOpen);

  useEffect(() => { lsSet("mdt-theme",   mode); },        [mode]);
  useEffect(() => { lsSet("mdt-sidebar", sidebarOpen); }, [sidebarOpen]);
  useEffect(() => { lsSet("mdt-exports", exports); },     [exports]);

  const handleExport = useCallback((entry) => {
    setExports(prev => [entry, ...prev].slice(0, 50));
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...entry, id }]);
  }, []);

  const handleClearExports = useCallback(() => {
    setExports([]);
    lsSet("mdt-exports", []);
  }, []);

  return (
    <HelmetProvider>
      <style>{css}</style>

      {/* Skip to content */}
      <a href="#main-content" className="skip-link">Skip to content</a>

      <button className="float-theme-btn"
        onClick={() => setMode(m => m === "dark" ? "light" : "dark")}
        aria-label="Toggle color theme"
        title={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}>
        <span className="float-theme-icon">{t.sunMoon}</span>
        <span className="float-theme-label">{t.themeLbl}</span>
      </button>

      <ExportToastContainer toasts={toasts} setToasts={setToasts} />

      <BrowserRouter>
        <AppLayout
          t={t}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          exports={exports}
          onExport={handleExport}
          onClearExports={handleClearExports}
        />
      </BrowserRouter>
    </HelmetProvider>
  );
}
